import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "../store";
import {
  AccountingActivitySyncState,
  AccountingConnection,
  AccountingSyncResponse,
  ActivityProviderRecord,
  connectXero,
  disconnectXeroV2,
  getAccountingConnections,
  getActivityAccountingIntegrationRecords,
  saveXeroConfig,
  SaveXeroConfigRequest,
  syncActivity,
  testXeroConnection,
  TestXeroConnectionResult
} from "src/network/graphql/xeroServices";
import {
  connectMyob,
  disconnectMyob,
  saveMyobConfig,
  SaveMyobConfigRequest,
  testMyobConnection
} from "src/network/graphql/myobServices";
import {
  connectQuickBooks,
  disconnectQuickBooks,
  saveQuickBooksConfig,
  SaveQuickBooksConfigRequest,
  setQuickBooksBankAccount,
  SetQuickBooksBankAccountRequest,
  testQuickBooksConnection
} from "src/network/graphql/quickbooksServices";

interface AccountingIntegrationsState {
  connections: AccountingConnection[];
  loading: boolean;
  error: unknown;
  connectLoading: boolean;
  disconnectLoading: boolean;
  testLoading: boolean;
  testResult: TestXeroConnectionResult | null;
  testErrorType: string | null;
  saveConfigLoading: boolean;
  activityRecords: Record<string, ActivityProviderRecord[]>;
  activityRecordsLoading: Record<string, boolean>;
  syncActivityLoading: Record<string, boolean>;
}

const initialState: AccountingIntegrationsState = {
  connections: [],
  loading: false,
  error: null,
  connectLoading: false,
  disconnectLoading: false,
  testLoading: false,
  testResult: null,
  testErrorType: null,
  saveConfigLoading: false,
  activityRecords: {},
  activityRecordsLoading: {},
  syncActivityLoading: {}
};

const accountingIntegrationsSlice = createSlice({
  name: "accountingIntegrations",
  initialState,
  reducers: {
    fetchConnectionsStart: (state) => {
      state.loading = true;
    },
    fetchConnectionsSuccess: (state, action: PayloadAction<AccountingConnection[]>) => {
      state.loading = false;
      state.connections = action.payload;
    },
    fetchConnectionsFail: (state, action) => {
      state.loading = false;
      state.connections = [];
      state.error = action.payload;
    },
    connectStart: (state) => {
      state.connectLoading = true;
    },
    connectFinish: (state) => {
      state.connectLoading = false;
    },
    disconnectStart: (state) => {
      state.disconnectLoading = true;
    },
    disconnectFinish: (state) => {
      state.disconnectLoading = false;
    },
    testStart: (state) => {
      state.testLoading = true;
      state.testResult = null;
      state.testErrorType = null;
    },
    testSuccess: (state, action: PayloadAction<TestXeroConnectionResult>) => {
      state.testLoading = false;
      state.testResult = action.payload;
      state.testErrorType = null;
    },
    testFail: (state, action: PayloadAction<string>) => {
      state.testLoading = false;
      state.testResult = null;
      state.testErrorType = action.payload;
    },
    resetTestResult: (state) => {
      state.testResult = null;
      state.testErrorType = null;
    },
    upsertConnection: (state, action: PayloadAction<AccountingConnection>) => {
      const idx = state.connections.findIndex(
        (c) => c.provider === action.payload.provider && c.organisation_id === action.payload.organisation_id
      );

      if (idx === -1) state.connections.push(action.payload);
      else state.connections[idx] = action.payload;
    },
    saveConfigStart: (state) => {
      state.saveConfigLoading = true;
    },
    saveConfigFinish: (state) => {
      state.saveConfigLoading = false;
    },
    fetchActivityRecordsStart: (state, action: PayloadAction<string>) => {
      state.activityRecordsLoading[action.payload] = true;
    },
    fetchActivityRecordsSuccess: (
      state,
      action: PayloadAction<{ activity_id: string; records: ActivityProviderRecord[] }>
    ) => {
      state.activityRecordsLoading[action.payload.activity_id] = false;
      state.activityRecords[action.payload.activity_id] = action.payload.records;
    },
    fetchActivityRecordsFail: (state, action: PayloadAction<string>) => {
      state.activityRecordsLoading[action.payload] = false;
    },
    syncActivityStart: (state, action: PayloadAction<string>) => {
      state.syncActivityLoading[action.payload] = true;
    },
    syncActivityFinish: (state, action: PayloadAction<string>) => {
      state.syncActivityLoading[action.payload] = false;
    }
  }
});

export const {
  fetchConnectionsStart,
  fetchConnectionsSuccess,
  fetchConnectionsFail,
  connectStart,
  connectFinish,
  disconnectStart,
  disconnectFinish,
  testStart,
  testSuccess,
  testFail,
  resetTestResult,
  upsertConnection,
  saveConfigStart,
  saveConfigFinish,
  fetchActivityRecordsStart,
  fetchActivityRecordsSuccess,
  fetchActivityRecordsFail,
  syncActivityStart,
  syncActivityFinish
} = accountingIntegrationsSlice.actions;

export const fetchAccountingConnectionsAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchConnectionsStart());
    try {
      const response = await getAccountingConnections();
      const list: AccountingConnection[] = response?.data?.get_accounting_connections ?? [];

      dispatch(fetchConnectionsSuccess(list));
      return list;
    } catch (error) {
      dispatch(fetchConnectionsFail(error));
      return null;
    }
  };
};

export const connectXeroAction = (callback_url: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectStart());
    try {
      const response = await connectXero(callback_url);

      dispatch(connectFinish());
      return response;
    } catch (error) {
      dispatch(connectFinish());
      return null;
    }
  };
};

export const disconnectXeroV2Action = (cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(disconnectStart());
    try {
      await disconnectXeroV2();
      dispatch(disconnectFinish());
      if (cb) cb();
      return true;
    } catch (error) {
      dispatch(disconnectFinish());
      return false;
    }
  };
};

export const testXeroConnectionAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(testStart());
    try {
      const response = await testXeroConnection();
      const result: TestXeroConnectionResult | undefined = response?.data?.test_xero_connection;

      if (!result) {
        dispatch(testFail("UNKNOWN"));
        return null;
      }
      dispatch(testSuccess(result));
      return result;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const errType = status === 504 || status === 408 || status === 0 ? "NETWORK" : "UNKNOWN";

      dispatch(testFail(errType));
      return null;
    }
  };
};

export const saveXeroConfigAction = (data: SaveXeroConfigRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveConfigStart());
    try {
      const response = await saveXeroConfig(data);
      const connection: AccountingConnection | undefined = response?.data?.save_xero_config;

      if (connection) dispatch(upsertConnection(connection));
      immediateToast("success", {
        message: "Transaction preferences updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(saveConfigFinish());
      if (cb) cb();
      return connection ?? null;
    } catch (error) {
      dispatch(saveConfigFinish());
      return null;
    }
  };
};

// ── MYOB actions ──────────────────────────────────────────────────────────
// These mirror the Xero actions above. The accounting-integrations layer is
// provider-generic, so they share the same slice state (connect/disconnect/
// test/saveConfig flags and the connections list).

export const connectMyobAction = (callback_url: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectStart());
    try {
      const response = await connectMyob(callback_url);

      dispatch(connectFinish());
      return response;
    } catch (error) {
      dispatch(connectFinish());
      return null;
    }
  };
};

export const connectMyobToOrganisation = (callback_url: string, cb?: () => void, fcb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectStart());
    try {
      await connectMyob(callback_url);

      dispatch(connectFinish());
      if (cb) cb();
    } catch (error) {
      dispatch(connectFinish());
      if (fcb) fcb();
    }
  };
};

export const disconnectMyobV2Action = (cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(disconnectStart());
    try {
      await disconnectMyob();
      dispatch(disconnectFinish());
      if (cb) cb();
      return true;
    } catch (error) {
      dispatch(disconnectFinish());
      return false;
    }
  };
};

export const testMyobConnectionAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(testStart());
    try {
      const response = await testMyobConnection();
      const result: TestXeroConnectionResult | undefined = response?.data?.test_myob_connection;

      if (!result) {
        dispatch(testFail("UNKNOWN"));
        return null;
      }
      dispatch(testSuccess(result));
      return result;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const errType = status === 504 || status === 408 || status === 0 ? "NETWORK" : "UNKNOWN";

      dispatch(testFail(errType));
      return null;
    }
  };
};

// ---- QuickBooks Online — mirrors the Xero / MYOB actions above (shared slice state).

export const connectQuickBooksToOrganisation = (callback_url: string, cb?: () => void, fcb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectStart());
    try {
      await connectQuickBooks(callback_url);

      dispatch(connectFinish());
      if (cb) cb();
    } catch (error) {
      dispatch(connectFinish());
      if (fcb) fcb();
    }
  };
};

export const disconnectQuickBooksV2Action = (cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(disconnectStart());
    try {
      await disconnectQuickBooks();
      dispatch(disconnectFinish());
      if (cb) cb();
      return true;
    } catch (error) {
      dispatch(disconnectFinish());
      return false;
    }
  };
};

export const testQuickBooksConnectionAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(testStart());
    try {
      const response = await testQuickBooksConnection();
      const result: TestXeroConnectionResult | undefined = response?.data?.test_quickbooks_connection;

      if (!result) {
        dispatch(testFail("UNKNOWN"));
        return null;
      }
      dispatch(testSuccess(result));
      return result;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const errType = status === 504 || status === 408 || status === 0 ? "NETWORK" : "UNKNOWN";

      dispatch(testFail(errType));
      return null;
    }
  };
};

export const saveQuickBooksConfigAction = (data: SaveQuickBooksConfigRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveConfigStart());
    try {
      const response = await saveQuickBooksConfig(data);
      const connection: AccountingConnection | undefined = response?.data?.save_quickbooks_config;

      if (connection) dispatch(upsertConnection(connection));
      immediateToast("success", {
        message: "Transaction preferences updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(saveConfigFinish());
      if (cb) cb();
      return connection ?? null;
    } catch (error) {
      dispatch(saveConfigFinish());
      return null;
    }
  };
};

/**
 * Set the QuickBooks paid-from account (an existing QBO Bank / Credit Card
 * account). Reuses the save-config loading flag; the caller refetches the
 * connections so the Bank Account tab reflects `config.default_bank_account`.
 */
export const setQuickBooksBankAccountAction = (data: SetQuickBooksBankAccountRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveConfigStart());
    try {
      await setQuickBooksBankAccount(data);
      immediateToast("success", {
        message: "Bank account updated",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(saveConfigFinish());
      if (cb) cb();
      return true;
    } catch (error) {
      dispatch(saveConfigFinish());
      return false;
    }
  };
};

export const saveMyobConfigAction = (data: SaveMyobConfigRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveConfigStart());
    try {
      const response = await saveMyobConfig(data);
      const connection: AccountingConnection | undefined = response?.data?.save_myob_config;

      if (connection) dispatch(upsertConnection(connection));
      immediateToast("success", {
        message: "Transaction preferences updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(saveConfigFinish());
      if (cb) cb();
      return connection ?? null;
    } catch (error) {
      dispatch(saveConfigFinish());
      return null;
    }
  };
};

export const fetchActivityAccountingRecordsAction = (activity_id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchActivityRecordsStart(activity_id));
    try {
      const response = await getActivityAccountingIntegrationRecords(activity_id);
      const result: AccountingActivitySyncState | undefined =
        response?.data?.get_activity_accounting_integration_records;
      const records = result?.records ?? [];

      dispatch(fetchActivityRecordsSuccess({ activity_id, records }));
      return records;
    } catch (error) {
      dispatch(fetchActivityRecordsFail(activity_id));
      return null;
    }
  };
};

export const syncActivityAction = (activity_id: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(syncActivityStart(activity_id));
    try {
      const response = await syncActivity(activity_id);
      const result: AccountingSyncResponse | undefined = response?.data?.sync_activity;
      const outcome = result?.outcomes?.[0];

      if (outcome && outcome.status !== "SUCCEEDED") {
        const message =
          outcome.error?.message ||
          (outcome.status === "INELIGIBLE"
            ? outcome.eligibility_reason || "Activity is not eligible to sync."
            : `Sync ${outcome.status.toLowerCase().replace(/_/g, " ")}.`);

        immediateToast("error", { message, timeout: 4000, position: "topCenter" });
      }

      dispatch(syncActivityFinish(activity_id));
      await dispatch(fetchActivityAccountingRecordsAction(activity_id));
      if (cb) cb();
      return result ?? null;
    } catch (error) {
      dispatch(syncActivityFinish(activity_id));
      return null;
    }
  };
};

export default accountingIntegrationsSlice.reducer;
